import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold ring-offset-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-[hsl(var(--primary))] text-white hover:brightness-90 shadow-sm hover:shadow-md",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        outline:
          "hairline bg-white text-foreground hover:bg-muted hover:text-foreground transition-colors disabled:bg-slate-50 disabled:text-slate-400",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[hsl(var(--secondary))]/90 shadow-sm hover:shadow-md",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "outline-secondary": "hairline bg-white text-foreground hover:bg-muted transition-colors disabled:bg-slate-50 disabled:text-slate-400",
        "outline-muted": "hairline bg-white text-slate-700 hover:bg-slate-50 transition-colors disabled:bg-slate-50 disabled:text-slate-400",
        "brand-outline": "border-2 border-primary bg-gradient-to-r from-white to-slate-50/50 text-primary hover:bg-gradient-primary hover:text-white hover:shadow-md transition-all duration-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:border-slate-200 disabled:cursor-not-allowed",
        link: "text-secondary underline-offset-4 hover:underline",
        gradient: "bg-gradient-primary text-white shadow-sm hover:shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
